import PropTypes from "prop-types";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputLabel, MenuItem, Select,
    TextField
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React from "react";
import {useTranslation} from "react-i18next";
import {makeRequest} from "../../services/requestService";
import {API_URL} from "../../constants";

export default function CollectionDialog(params) {
    const {t} = useTranslation('common');
    const [name, setName] = React.useState("");
    const [description, setDescription] = React.useState("");
    const [visibility, setVisibility] = React.useState("PRIVATE");

    React.useEffect(() => {
        reset();
    }, [params.open])

    function reset() {
        if (params.mode === "CREATE") {
            setName("");
            setDescription("");
            setVisibility("PRIVATE")
        } else if (params.mode === "EDIT") {
            if (params.collection?.name) {
                setName(params.collection.name);
            }
            if (params.collection?.description) {
                setDescription(params.collection.description);
            }
            if (params.collection?.visibility) {
                setVisibility(params.collection.visibility);
            }
        }
    }

    function handleClose() {
        reset();
        params.onClose();
    }

    async function handleCreate() {
        const body = {name, description, visibility};
        const headers = {
            'Accepts': 'application/json',
            'Content-Type': 'application/json',
        };

        let url;
        let method;
        if (params.mode === "CREATE") {
            url = `${API_URL}/collection`;
            method = "POST";
        } else if (params.mode === "EDIT") {
            url = `${API_URL}/collection/${params.collection.id}`;
            method = "PUT";
        }

        const response = await makeRequest(url, method, JSON.stringify(body), headers);

        if (response.status !== 200) {
            return;
        }

        reset();
        params.onCreated(response.data);
    }

    return (
        <Dialog open={params.open} onClose={handleClose} onBackdropClick={handleClose} maxWidth="sm"
                fullWidth={true} {...params}>
            <DialogTitle sx={{m: 0, p: 2}}>
                {params.mode === "EDIT" ?
                    t("main.components.form.collection.title.edit") :
                    t("main.components.form.collection.title.create")}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon/>
            </IconButton>
            <DialogContent>
                <TextField
                    color="secondary"
                    variant="outlined"
                    margin="normal"
                    required
                    fullWidth
                    id="name"
                    label={t('main.components.form.collection.placeholder.name')}
                    name="name"
                    autoFocus
                    onChange={(event) => setName(event.target.value)}
                    value={name}
                />
                <TextField
                    color="secondary"
                    variant="outlined"
                    margin="normal"
                    required
                    fullWidth
                    id="description"
                    label={t('main.components.form.collection.placeholder.description')}
                    name="description"
                    onChange={(event) => setDescription(event.target.value)}
                    value={description}
                />
                <InputLabel
                    id="select-visibility-label">{t('main.components.form.collection.placeholder.visibility')}</InputLabel>
                <Select
                    color="secondary"
                    variant="outlined"
                    margin="normal"
                    required
                    fullWidth
                    label="select-visibility-label"
                    name="visibility"
                    onChange={(event) => setVisibility(event.target.value)}
                    value={visibility}
                >
                    <MenuItem value="PRIVATE">{t('main.components.form.collection.visibility.private')}</MenuItem>
                    <MenuItem value="PUBLIC">{t('main.components.form.collection.visibility.public')}</MenuItem>
                </Select>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>{t('main.components.saveDialog.close')}</Button>
                <Button type="submit" onClick={() => handleCreate()}>{t('main.components.saveDialog.save')}</Button>
            </DialogActions>
        </Dialog>
    );
}

CollectionDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onCreated: PropTypes.func.isRequired,
    mode: PropTypes.oneOf(["EDIT", "CREATE"]).isRequired,
    collection: PropTypes.any,
}